// pages/RoleLoginPage.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChefHat, ClipboardList, Lock, RefreshCw, Ticket, Wine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  getBusiness,
  getInstallCompletedAt,
  hydrateFromSnapshot,
  listOfflineCredentialUserIds,
  markInstallCompleted,
  readAll,
  readOne,
} from "@/lib/localDb";
import { enrollOfflineCredentials, loginFromLocalSnapshot, pingConnectivity, verifyOfflineLogin } from "@/lib/offlineAuth";
import { fetchBootstrapFromLan, getManualLanHost, lanBootstrapAvailable, setManualLanHost } from "@/lib/lanBootstrap";
import { refreshSnapshotNow } from "@/lib/snapshotRefresh";
import { getStoredBusinessCode, isStandaloneLaunch, setStoredBusinessCode, setStoredDeviceRole } from "@/lib/device";
import { API, clearSession, getSession, http, postWithRetry } from "@/app/core";
import { removeRoleSession, writeRoleSession } from "@/lib/authStorage";
import { armLoginChime, unlockAudio } from "@/app/chimes";
import { ThemeToggleButton } from "@/app/fullscreen";


const ROLE_TO_DASHBOARD = {
  staff: "/staff/dashboard",
  owner: "/owner/dashboard",
  kitchen: "/kitchen/dashboard",
  bar: "/bar/dashboard",
  retail: "/retail/dashboard",
};

const setSession = (role, value /* remember ignored */) => {
  // Session 26 — two-phase persistence:
  //
  //   Phase A (login just fired, dashboard not yet mounted):
  //     Stamp `_pending: true` and store in sessionStorage ONLY. If
  //     the destination dashboard crashes before it can call
  //     `confirmSession(role)`, the pending session is discarded on
  //     the next tab launch (sessionStorage is tab-scoped) and
  //     RoleLoginPage's redirect effect also clears it defensively.
  //
  //   Phase B (dashboard successfully rendered — see `confirmSession`):
  //     Promote to localStorage so the session survives page refresh
  //     AND tab-reopen. This is the correct restaurant behaviour —
  //     a kitchen or bar tablet must not require re-authentication
  //     after every screen off / soft reboot. Explicit logout still
  //     clears both stores. Idle-timeout enforcement is a separate
  //     concern handled by the sessionHeartbeat below.
  const stamped = value && typeof value === "object"
    ? { ...value, _pending: true, _pending_since: new Date().toISOString(), _login_at: new Date().toISOString() }
    : value;
  // Clear any previous confirmed session under this key first — a
  // fresh login must never resurrect an earlier user's data.
  removeRoleSession(role, "device");
  writeRoleSession(role, stamped, "tab");
};

export function RoleLoginPage({ role, runnerMode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedBusiness = (searchParams.get("business") || "").trim().toLowerCase();
  // Initial value priority: ?business=… URL param > previously-saved device
  // business code (PWA "remember the business code" requirement) > empty.
  const [businessCode, setBusinessCode] = useState(() => {
    const fromUrl = searchParams.get("business");
    if (fromUrl) return fromUrl;
    try { return getStoredBusinessCode() || ""; } catch (_) { return ""; }
  });
  const [rememberDevice, setRememberDevice] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Two-stage flow: 1) business code → fetch user list → 2) pick user + PIN.
  // `legacy=true` falls back to the old role-password screen for businesses
  // without users seeded yet (or when an operator explicitly opts in).
  //
  // Installed PWA behaviour: if the device already has a business code
  // saved (from the install link) AND we're running as a standalone app,
  // start at the PIN stage directly. The Business Code must never be
  // requested after install — the app "knows" its business.
  const isInstalledLaunch = (() => { try { return isStandaloneLaunch(); } catch (_) { return false; } })();
  const preSkipCode = Boolean(businessCode) && isInstalledLaunch;
  const [stage, setStage] = useState(preSkipCode ? "pin" : "code");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [pin, setPin] = useState("");
  const [legacy, setLegacy] = useState(false);
  const [legacyPassword, setLegacyPassword] = useState("");
  // Session 27.72 — Skeleton pre-auth download progress. When true,
  // the CardContent shows a "Preparing your workspace…" strip in place
  // of the PIN picker so the operator sees the download happen instead
  // of it being fire-and-forget in the background.
  const [preparingSnapshot, setPreparingSnapshot] = useState(false);
  const [prepareDetail, setPrepareDetail] = useState("");
  // Session 31.4 — LAN bootstrap (installed apps only): a device that has
  // never synced this business copies the workspace from the Coordinator
  // over Wi-Fi so it can sign in by PIN with no internet at all.
  const [lanCapable, setLanCapable] = useState(false);
  const [manualHostOpen, setManualHostOpen] = useState(false);
  const [manualHost, setManualHost] = useState(() => { try { return getManualLanHost() || ""; } catch (_) { return ""; } });
  useEffect(() => { lanBootstrapAvailable().then(setLanCapable).catch(() => setLanCapable(false)); }, []);

  const rolesForPicker = () => (
    runnerMode      ? ["runner"] :
    role === "owner"   ? ["owner", "manager", "supervisor"] :
    role === "staff"   ? ["waiter", "supervisor"] :
    role === "kitchen" ? ["kitchen"] :
    role === "bar"     ? ["bar"] :
    role === "retail"  ? ["retailer"] :
    []
  );

  /** Copy the workspace from a LAN coordinator into IndexedDB and return
   *  the user picker list for this role (or null when nothing was found). */
  const bootstrapWorkspaceFromLan = async (code) => {
    if (!isStandaloneLaunch()) return null;
    setPreparingSnapshot(true);
    setPrepareDetail("No internet — looking for a coordinator on this Wi-Fi…");
    try {
      const found = await fetchBootstrapFromLan({ businessCode: code, onStatus: setPrepareDetail });
      if (!found?.snapshot) return null;
      const snapBiz = String(found.snapshot.business?.slug || "").toLowerCase();
      if (snapBiz && code && snapBiz !== String(code).toLowerCase()) {
        toast.error(`The coordinator on this Wi-Fi runs “${found.snapshot.business?.name || snapBiz}”, not “${code}”.`);
        return null;
      }
      setPrepareDetail("Saving the workspace to this device…");
      await hydrateFromSnapshot(found.snapshot);
      try { await markInstallCompleted({ source: "lan-bootstrap", coordinator: found.coordinator?.info?.device_label || null }); } catch (_) { /* noop */ }
      const local = (await readAll("users")) || [];
      const set = new Set(rolesForPicker());
      const list = local
        .filter((u) => u && u.active !== false && (set.size === 0 || set.has(u.role)))
        .sort((a, b) => String(a.first_name || "").localeCompare(String(b.first_name || "")))
        .map((u) => ({
          id: u.id,
          first_name: u.first_name || "",
          last_name: u.last_name || "",
          full_name: (((u.first_name || "") + " " + (u.last_name || "")).trim() || u.username || ""),
          role: u.role,
          active: true,
          _offline_ready: Boolean(u.pin_hash),
        }));
      setPrepareDetail(`Workspace copied from ${found.coordinator?.info?.device_label || "the coordinator"} · ${local.length} users`);
      toast.success("Workspace copied from the coordinator over Wi-Fi", { description: "Sign in with your PIN — changes sync to the coordinator and to the cloud later." });
      return list;
    } finally {
      await new Promise((r) => setTimeout(r, 300));
      setPreparingSnapshot(false);
    }
  };

  useEffect(() => {
    const existingSession = getSession(role);
    if (!existingSession?.token) return;
    // Session 26 — never auto-redirect on a PENDING session. A pending
    // session means the last login attempt reached `setSession` but the
    // destination dashboard never successfully rendered (e.g. it
    // threw during hydration). Redirecting the user straight back
    // into it would trap them in a crash loop and prevent them from
    // picking a different waiter. Instead, drop the pending session
    // and let the picker rehydrate so a fresh sign-in can be tried.
    if (existingSession._pending === true) {
      try { clearSession(role, "pending_session_dropped_on_login_page"); } catch (_) { /* noop */ }
      return;
    }
    const existingSlug = (existingSession?.business?.slug || "").trim().toLowerCase();
    if (requestedBusiness && existingSlug && requestedBusiness !== existingSlug) {
      clearSession(role, "different_business_requested");
      return;
    }
    navigate(runnerMode ? "/runner/dashboard" : (ROLE_TO_DASHBOARD[role] || "/"), { replace: true });
  }, [navigate, role, requestedBusiness, runnerMode]);

  // On installed PWAs with a stored business code, silently fetch the
  // user list so the PIN stage renders instantly without the operator
  // having to type the business code.
  useEffect(() => {
    if (!preSkipCode) return;
    if (users.length > 0) return;
    fetchUsers(businessCode.trim().toLowerCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (code) => {
    setSubmitting(true);
    try {
      // Session 27.1 — PLATFORM-GATED behaviour.
      //
      // Only INSTALLED contexts (PWA / Capacitor APK) treat the local
      // IndexedDB as the primary source of truth. A regular browser
      // tab must always go to the network first — the local DB may be
      // empty, stale, or belong to a completely different business
      // that the same operator once installed as a PWA on the same
      // browser profile.
      //
      // Historical bug (Session 27 → 27.1 regression): fetching users
      // local-first from a regular browser could surface old cached
      // rosters after a PWA had been uninstalled or the business was
      // changed, and hand the user a fake "offline:" session token
      // (see handleLogin) that the cloud dashboard would then reject
      // on the next API call, causing a login-kick-out loop.
      const installed = isStandaloneLaunch();

      let list = null;

      if (installed) {
        // Installed: local IDB is authoritative. Try it first, refresh
        // in the background.
        try {
          const local = (await readAll("users")) || [];
          if (local.length > 0) {
            const enrolledIds = new Set(await listOfflineCredentialUserIds());
            const wantedRoles = (
              runnerMode      ? ["runner"] :
              role === "owner"   ? ["owner", "manager", "supervisor"] :
              role === "staff"   ? ["waiter", "supervisor"] :
              role === "kitchen" ? ["kitchen"] :
              role === "bar"     ? ["bar"] :
              role === "retail"  ? ["retailer"] :
              []
            );
            const set = new Set(wantedRoles);
            list = local
              .filter((u) => u && u.active !== false && (set.size === 0 || set.has(u.role)))
              .sort((a, b) => {
                const ea = enrolledIds.has(a.id) ? 0 : 1;
                const eb = enrolledIds.has(b.id) ? 0 : 1;
                if (ea !== eb) return ea - eb;
                return String(a.first_name || "").localeCompare(String(b.first_name || ""));
              })
              .map((u) => ({
                id: u.id,
                first_name: u.first_name || "",
                last_name: u.last_name || "",
                full_name: (((u.first_name || "") + " " + (u.last_name || "")).trim() || u.username || ""),
                role: u.role,
                active: true,
                _offline_ready: enrolledIds.has(u.id) || Boolean(u.pin_hash),
              }));
          }
        } catch (_) { list = null; }
      }

      // Regular browser OR installed device with no local roster yet →
      // network is authoritative. Failure surfaces normally to the
      // user (bad business code, no network, etc.).
      if (list === null || list.length === 0) {
        try {
          const r = await axios.get(
            `${API}/businesses/${encodeURIComponent(code)}/users`,
            { params: runnerMode ? { role: "runner" } : { scope: role }, timeout: 5000 },
          );
          const remote = Array.isArray(r.data?.users) ? r.data.users : [];
          list = remote;
        } catch (netErr) {
          const status = netErr?.response?.status;
          if (status && status >= 400 && status < 500) {
            throw netErr;
          }
          // Installed devices only: if the network is down and we had
          // no local data, degrade gracefully to an empty list — the
          // user cannot sign in until connectivity returns or an
          // install completes. Regular browser gets the error.
          if (!installed) throw netErr;
          list = [];
          // Session 31.4 — No internet and nothing cached yet: copy the
          // workspace from the Coordinator over Wi-Fi (native LAN
          // document store) so this fresh device can sign in by PIN.
          try {
            const lanUsers = await bootstrapWorkspaceFromLan(code);
            if (lanUsers) list = lanUsers;
          } catch (e) { console.warn("[app] LAN bootstrap failed:", e?.message || e); }
          if (list.length === 0) {
            toast.error("No internet and no coordinator found on this Wi-Fi", {
              description: "Open the app on the coordinator device first (kitchen/bar tablet), or tap “Coordinator address” below to enter its IP.",
              duration: 9000,
            });
          }
        }
      } else {
        // Installed + we rendered from local: fire a background refresh
        // so newly-added / newly-deactivated users show up on next mount.
        (async () => {
          try {
            const r = await axios.get(
              `${API}/businesses/${encodeURIComponent(code)}/users`,
              { params: runnerMode ? { role: "runner" } : { scope: role }, timeout: 4000 },
            );
            const remote = Array.isArray(r.data?.users) ? r.data.users : [];
            if (remote.length > 0) {
              setUsers((prev) => {
                const mergedIds = new Set(prev.map((u) => u.id));
                const additions = remote
                  .filter((u) => u && u.active !== false && !mergedIds.has(u.id))
                  .map((u) => ({
                    id: u.id,
                    first_name: u.first_name || "",
                    last_name: u.last_name || "",
                    full_name: (((u.first_name || "") + " " + (u.last_name || "")).trim() || u.username || ""),
                    role: u.role,
                    active: true,
                    _offline_ready: false,
                  }));
                if (additions.length === 0) return prev;
                return [...prev, ...additions];
              });
            }
          } catch (_) { /* background — silent */ }
        })();
      }
      list = list || [];
      setUsers(list);
      // Session 27.62 — Pre-login snapshot refresh. The moment the
      // business code has been validated (users list came back
      // successfully), fire a silent snapshot refresh in the
      // background so latest menu / users / config land in IndexedDB
      // BEFORE the operator finishes typing their PIN. This means if
      // WiFi drops between "business code entered" and "PIN entered"
      // the offline sign-in still has fresh data to authenticate
      // against. Fire-and-forget — never blocks the picker render.
      // Only meaningful on installed contexts (regular browsers
      // don't run local-first).
      if (installed) {
        try {
          // refreshSnapshotNow reads any cached role token via the
          // tokenSupplier configured at boot. On a fresh device with
          // no prior sessions the call is a silent no-op — that's
          // fine, the post-login snapshot fetch will handle the
          // first-time case as before.
          refreshSnapshotNow();
        } catch (e) { console.warn("[app] pre-auth snapshot refresh failed:", e?.message || e); }
        // Session 27.72 — Public pre-auth snapshot with VISIBLE progress.
        // The prior fire-and-forget approach meant users saw nothing
        // happen after typing their business code — then a big download
        // popped up AFTER signing in. Now we AWAIT the skeleton fetch
        // (with a short "Preparing your workspace…" indicator) BEFORE
        // moving to the PIN stage so the sequence feels correct:
        //   business code → visible prep → PIN → instant dashboard.
        // Bounded to ~8s so a slow connection doesn't stall the PIN
        // screen indefinitely.
        try {
          setPreparingSnapshot(true);
          setPrepareDetail("Downloading menu, tables, and settings…");
          const skeletonPromise = http.get(
            `/public/business/${encodeURIComponent(code)}/skeleton`,
            { timeout: 8000 },
          );
          const skeleton = await Promise.race([
            skeletonPromise,
            new Promise((resolve) => setTimeout(() => resolve(null), 8500)),
          ]);
          if (skeleton && skeleton.data && skeleton.data.business) {
            setPrepareDetail("Saving your workspace to this device…");
            let totalRecords = 0;
            try {
              const t = skeleton.data.totals || {};
              totalRecords = Object.values(t).reduce((a, b) => a + Number(b || 0), 0);
            } catch (_) { /* noop */ }
            await hydrateFromSnapshot(skeleton.data, {
              onProgress: ({ done: d, total, name, count }) => {
                const pretty = String(name || "")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());
                setPrepareDetail(
                  `${pretty} · ${Number(count || 0).toLocaleString()} · ${d}/${total}`,
                );
              },
            });
            setPrepareDetail(
              totalRecords > 0
                ? `Workspace ready · ${totalRecords.toLocaleString()} records cached`
                : "Workspace ready",
            );
          }
        } catch (e) { console.warn("[app] pre-auth skeleton snapshot failed:", e?.message || e); }
        finally {
          // Small pause so operators can see the "Ready" state instead
          // of a flash. Keeps the transition feeling deliberate.
          await new Promise((r) => setTimeout(r, 250));
          setPreparingSnapshot(false);
        }
      }
      if (!list.length) {
        // Nothing to pick — fall straight to legacy password.
        setLegacy(true);
        setStage("pin");
        // For Kitchen / Bar legacy logins the heading itself ("Sign in
        // with the Kitchen Password") already tells the user what to
        // do, so we suppress the duplicate "No user accounts yet" toast
        // there. We still surface it on staff / owner login screens where
        // the absence of PIN users is more surprising.
        if (role !== "kitchen" && role !== "bar" && role !== "retail") {
          toast.message("No user accounts yet — sign in with the business password.");
        }
      } else {
        setStage("pin");
        setSelectedUserId(list[0].id);
      }
    } catch (error) {
      const httpStatus = error?.response?.status;
      const serverDetail = error?.response?.data?.detail;
      if (httpStatus === 404) {
        // Server reached but slug unknown — likely a typo.
        toast.error("Business code not found — double-check the code and try again.", { duration: 6000 });
      } else if (!httpStatus) {
        // No response at all = network/connectivity issue.
        toast.error("Connection error — check your internet and tap Next again.", { duration: 6000 });
      } else {
        toast.error(apiErrorMessage(error, "Sign-in failed — please try again."), { duration: 6000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeContinue = (event) => {
    event.preventDefault();
    if (!businessCode.trim()) {
      toast.error("Enter your business code.");
      return;
    }
    fetchUsers(businessCode.trim().toLowerCase());
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    // Unlock the shared AudioContext inside this real user gesture so
    // chimes work on the destination dashboard (browsers gate audio
    // until a gesture resumes the context).
    unlockAudio();
    setSubmitting(true);
    // Session 27.1 — LOCAL-FIRST LOGIN, PROPERLY GATED.
    //
    // The local-first authentication path is ONLY sound on installed
    // devices (PWA / Capacitor APK) where the local IndexedDB is the
    // authoritative replica of the business. A regular browser tab
    // must go straight to the cloud login — the local DB may be
    // stale (from a previous PWA install on this profile), empty, or
    // belong to a different business, and minting a client-side
    // "offline:" token in that context would make every subsequent
    // dashboard API call fail with 401 → login-kick-out loop.
    //
    // Additional refinement: even on installed devices we PREFER the
    // online login when connectivity is available. That way the
    // session token stored in sessionStorage is a real server bearer
    // that unlocks every mutation & dashboard fetch. We only mint a
    // client-side offline session when the network probe fails.
    //
    //   • Regular browser        → skip local, straight to online.
    //   • Installed + online     → online login first, local as fallback.
    //   • Installed + offline    → local PBKDF2 → local bcrypt-vs-snapshot.
    const installedContext = (() => {
      try { return isStandaloneLaunch(); } catch (_) { return false; }
    })();

    try {
      if (installedContext && !legacy && selectedUserId && pin.trim()) {
        // Quick connectivity probe so we know whether to attempt the
        // network first. 1.5s is short enough that offline devices
        // don't perceive it as a boot-blocker but long enough to
        // avoid false positives on slow LTE.
        let online = true;
        try { online = await pingConnectivity({ timeoutMs: 1500 }); }
        catch (_) { online = false; }

        if (!online) {
          // OFFLINE PATH — try the local verifiers. Never mint a fake
          // token when the network is actually reachable; a fake token
          // would just poison all downstream requests.
          const localUser = users.find((u) => u.id === selectedUserId) || null;
          const localBusiness = await getBusiness();
          let offlineSession = null;
          try {
            offlineSession = await verifyOfflineLogin({
              user: localUser, pin, role, business: localBusiness,
            });
          } catch (_) { offlineSession = null; }

          let localUserRec = null;
          try { localUserRec = await readOne("users", String(selectedUserId)); }
          catch (_) { localUserRec = null; }
          const localHasHash = Boolean(localUserRec && localUserRec.pin_hash);

          if (!offlineSession) {
            try {
              offlineSession = await loginFromLocalSnapshot({
                userId: selectedUserId, pin, role,
              });
            } catch (_) { offlineSession = null; }
          }
          // Session 31.4 — Never-synced user on this device: copy the
          // workspace from the LAN coordinator, then verify the PIN
          // against the freshly copied hash.
          if (!offlineSession && !localHasHash) {
            try {
              const lanUsers = await bootstrapWorkspaceFromLan(businessCode.trim().toLowerCase());
              if (lanUsers) {
                setUsers(lanUsers);
                offlineSession = await loginFromLocalSnapshot({ userId: selectedUserId, pin, role });
                if (!offlineSession) {
                  toast.error("Wrong PIN", { description: "Try again, or ask an owner to reset your PIN." });
                  setSubmitting(false);
                  return;
                }
              }
            } catch (_) { offlineSession = null; }
          }

          if (offlineSession) {
            setSession(role, offlineSession, rememberDevice);
            try {
              setStoredBusinessCode(businessCode.trim().toLowerCase());
              setStoredDeviceRole(role);
            } catch (_) { /* noop */ }
            armLoginChime();
            toast.success("Signed in offline", {
              description: "Changes will sync when the internet returns.",
            });
            navigate(runnerMode ? "/runner/dashboard" : (ROLE_TO_DASHBOARD[role] || "/"), { replace: true });
            setSubmitting(false);
            return;
          }

          if (localHasHash) {
            toast.error("Wrong PIN", {
              description: "Try again, or ask an owner to reset your PIN.",
            });
            setSubmitting(false);
            return;
          }
          // Local user unknown → tell the operator they need connectivity.
          toast.error("Cannot sign in offline", {
            description: "This user isn't on this device yet and no coordinator was found on this Wi-Fi. Start the coordinator device first, enter its address below, or connect to the internet once.",
            duration: 9000,
          });
          setSubmitting(false);
          return;
        }
        // If we got here we're installed AND online → fall through to
        // the normal online login below (which returns a real server
        // token). No offline session is minted; the dashboard works
        // exactly like the browser path.
      }

      let response;
      let enteredPin = null;
      // Session 31.4 — Installed device + no connectivity: the cloud
      // login would retry for ~60 s and look frozen. Fail fast with a
      // clear message instead (business passwords are never cached on
      // devices — offline sign-in is by user PIN).
      const loginConfig = installedContext ? { timeout: 8000 } : undefined;
      const loginRetry = installedContext ? { attempts: 1 } : undefined;
      if (installedContext && legacy) {
        let onlineNow = true;
        try { onlineNow = await pingConnectivity({ timeoutMs: 1500 }); } catch (_) { onlineNow = false; }
        if (!onlineNow) {
          toast.error("No internet — use your user PIN to sign in offline", {
            description: "Business passwords need the internet. Switch to the user list and sign in with a PIN; a fresh device can copy the workspace from the coordinator on this Wi-Fi.",
            duration: 9000,
          });
          setSubmitting(false);
          return;
        }
      }
      if (legacy) {
        if (!legacyPassword.trim()) { toast.error("Enter the password."); setSubmitting(false); return; }
        // Session 68 — the server requires at least 4 characters; say so
        // in plain words instead of surfacing a validation payload.
        if (legacyPassword.trim().length < 4) { toast.error("The password must be at least 4 characters."); setSubmitting(false); return; }
        // Session 27.30 — Login is idempotent (same credentials return
        // the same result). Use postWithRetry so a single Cloudflare
        // 520/521/522 on the sign-in POST retries up to 3× with
        // exponential backoff (400/900/2000ms) instead of showing
        // a scary "could not parse" error on the first hiccup.
        response = await postWithRetry("/auth/login", {
          business_code: businessCode.trim().toLowerCase(),
          password: legacyPassword,
          role: runnerMode ? "runner" : role,
        }, loginConfig, loginRetry);
        setSession(role, response.data, rememberDevice);
      } else {
        if (!selectedUserId) { toast.error("Pick a user."); setSubmitting(false); return; }
        if (!pin.trim()) { toast.error("Enter your PIN."); setSubmitting(false); return; }
        response = await postWithRetry("/auth/login-user", {
          business_code: businessCode.trim().toLowerCase(),
          user_id: selectedUserId,
          pin,
        }, loginConfig, loginRetry);
        enteredPin = pin;
        setSession(role, response.data, rememberDevice);
      }
      // Persist for PWA "remember device" — next launch can jump straight
      // to this role's login (or dashboard if the session is still valid).
      try {
        setStoredBusinessCode(businessCode.trim().toLowerCase());
        setStoredDeviceRole(role);
      } catch (_) { /* localStorage may be disabled */ }
      // Enroll offline credentials in the background — non-blocking so
      // login latency isn't affected by a slow PBKDF2 round-trip. If it
      // fails, the user just falls back to online login next time.
      //
      // Session 25.7 — we WAIT briefly (max ~3s) for enrollment to
      // finish IF an install-setup is about to run. That way the
      // Offline Readiness check downstream sees the enrolment as
      // completed instead of racing against it (which caused the
      // "11/12 checks passing" report on production). If enrollment
      // is slow or fails, we still proceed — the check is informational
      // anyway.
      let enrollPromise = null;
      if (!legacy && enteredPin && response?.data?.token) {
        enrollPromise = enrollOfflineCredentials({
          token: response.data.token,
          pin: enteredPin,
          deviceLabel: `${role}-${(response.data.business?.slug || businessCode || "").toString()}`,
        }).catch(() => null);
      }
      armLoginChime();
      // Session 27.10 — kick off the FULL business snapshot download
      // immediately after a successful sign-in. This is what makes
      // "download absolutely everything" fast: menu, categories,
      // tables, users, inventory, sessions, orders, settings — every
      // business-scoped collection lands in IndexedDB within seconds
      // of login. Fire-and-forget so login latency isn't affected.
      try { refreshSnapshotNow(); } catch (_) { /* noop */ }
      // Decide the landing route: if this device has never completed
      // an install (no local snapshot yet), route through the install
      // progress screen so the operator sees the full sync happen
      // before the dashboard opens. Otherwise straight to the dashboard.
      let landingPath = runnerMode ? "/runner/dashboard" : (ROLE_TO_DASHBOARD[role] || "/");
      let goingToInstallSetup = false;
      // Session 25.10 — only force the install/setup detour on
      // installed PWAs. Signing in from a regular browser tab (e.g. an
      // owner previewing the app from their laptop) should go straight
      // to the dashboard without downloading the whole business
      // database.
      let isInstalled = false;
      try { isInstalled = isStandaloneLaunch(); } catch (_) { isInstalled = false; }
      try {
        const installedAt = await getInstallCompletedAt();
        if (isInstalled && !installedAt) {
          landingPath = `/install/setup?next=${encodeURIComponent(landingPath)}`;
          goingToInstallSetup = true;
        } else if (isInstalled && installedAt) {
          // Session 27.62 — Device has ALREADY completed the initial
          // install once. The old flow was re-running the entire
          // "Downloading business data" wizard on every login inside
          // the APK because IDB meta writes weren't sticking across
          // relaunches. Now that localStorage is the source of truth
          // for the install-completed flag, we can trust it and skip
          // the wizard entirely. A tiny non-blocking toast tells the
          // operator we're pulling any changes since last login in
          // the background — the dashboard opens immediately.
          try {
            toast.message("Syncing latest menu…", { duration: 1800 });
          } catch (_) { /* noop */ }
          try { refreshSnapshotNow(); } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
      // If we're about to run install-setup, briefly wait for the
      // offline-credential enrolment to finish (cap at 2.5s) so the
      // readiness check downstream sees the enrolment as complete
      // instead of racing against it. Even if enrolment fails, we
      // proceed — the check is informational (Session 25.7 change).
      if (goingToInstallSetup && enrollPromise) {
        try {
          await Promise.race([
            enrollPromise,
            new Promise((resolve) => setTimeout(resolve, 2500)),
          ]);
        } catch (_) { /* noop */ }
      }
      navigate(landingPath, { replace: true });
    } catch (error) {
      if (!error?.response) {
        // Network-level failure (no internet / router without WAN).
        toast.error("Can't reach the server", {
          description: installedContext
            ? "No internet. Sign in with your user PIN — this device works offline and syncs to the coordinator on this Wi-Fi."
            : "Check the internet connection and try again.",
          duration: 8000,
        });
      } else {
        toast.error(apiErrorMessage(error, "Login failed."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const ROLE_COPY = {
    staff: { badge: "Staff access", headline: "Manage tables and orders.", title: "Staff login" },
    owner: { badge: "Owner / Manager access", headline: "Manage menu and business settings.", title: "Owner / Manager login" },
    kitchen: { badge: "Kitchen access", headline: "See incoming kitchen tickets in real time.", title: "Kitchen login" },
    bar: { badge: "Bar access", headline: "See incoming drink tickets in real time.", title: "Bar login" },
    retail: { badge: "Retail access", headline: "Ring up sales fast at the counter.", title: "Retail login" },
  };
  const copy = runnerMode
    ? { badge: "Runner access", headline: "See ready orders and deliver to tables.", title: "Runner login" }
    : (ROLE_COPY[role] || ROLE_COPY.staff);
  const features = role === "kitchen"
    ? [
        { icon: Lock, label: "Private business code" },
        { icon: RefreshCw, label: "Live ticket updates" },
        { icon: ChefHat, label: "Kitchen-only view" },
      ]
    : role === "bar"
    ? [
        { icon: Lock, label: "Private business code" },
        { icon: RefreshCw, label: "Live drink tickets" },
        { icon: Wine, label: "Bar-only view" },
      ]
    : [
        { icon: Lock, label: "Private business code" },
        { icon: RefreshCw, label: role === "staff" ? "Live table view" : "Fast updates" },
        { icon: role === "staff" ? ClipboardList : Ticket, label: role === "staff" ? "Table workflow" : "Menu + tags" },
      ];

  const ROLE_BADGE_TONE = {
    owner: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
    manager: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
    supervisor: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    waiter: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    staff: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    kitchen: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
    bar: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
    retailer: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30",
    admin: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  };

  return (
    <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-6 sm:px-6 sm:py-10">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggleButton />
      </div>
      {/* Session 27.3 — POS-focused login: single sign-in card only.
          Removed the left-hand marketing panel ("Live ticket updates",
          "Menu + tags" etc.) — operators told us they don't need it
          on the login screen. */}
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-md items-center justify-center">
        <Card className="w-full rounded-[28px] border-border bg-card p-1 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-3">
            <Badge className="mb-3 w-fit bg-[hsl(var(--primary))] text-primary-foreground">
              {copy.badge}
            </Badge>
            <CardTitle className="text-2xl">{copy.title}</CardTitle>
            <CardDescription>{preparingSnapshot ? "Setting up this device so sign-in stays instant." : stage === "code" ? "Enter the business code to continue." : (legacy ? (runnerMode ? "Sign in with the Runner Password." : role === "kitchen" ? "Sign in with the Kitchen Password." : role === "bar" ? "Sign in with the Bar Password." : role === "retail" ? "Sign in with the Retail Password." : role === "staff" ? "Sign in with the Staff Password." : "Sign in with the business password.") : "Pick your name and enter your PIN.")}</CardDescription>
          </CardHeader>
          <CardContent>
            {preparingSnapshot ? (
              <div
                className="space-y-4 rounded-2xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-5 text-center"
                data-testid={`${role}-preparing-workspace`}
                role="status"
                aria-live="polite"
              >
                {/* Session 30.34 — one spinner, one line. The per-stage
                    detail text was visual noise on a screen the operator
                    only sees for a few seconds. */}
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--accent))]/15">
                  <RefreshCw className="h-5 w-5 animate-spin text-[hsl(var(--accent))]" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold">One moment…</p>
                <p className="sr-only" data-testid={`${role}-preparing-workspace-detail`}>{prepareDetail || "Getting things ready for offline use…"}</p>
              </div>
            ) : stage === "code" ? (
              <form className="space-y-4" onSubmit={handleCodeContinue}>
                <div className="space-y-2">
                  <Label htmlFor={`${role}-business-code-input`}>Business code</Label>
                  <Input
                    id={`${role}-business-code-input`}
                    className="h-11 rounded-xl bg-background"
                    value={businessCode}
                    onChange={(event) => setBusinessCode(event.target.value.trim().toLowerCase())}
                    placeholder="Your business code"
                    autoComplete="username"
                    data-testid={`${role}-business-code-input`}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid={`${role}-login-next-button`}
                >
                  {submitting ? "Loading users…" : "Next"}
                </Button>
                {lanCapable ? (
                  /* Session 31.4 — offline Wi-Fi fallback: point this device at the coordinator. */
                  <div className="space-y-2 text-xs" data-testid={`${role}-lan-coordinator-block`}>
                    <button type="button" onClick={() => setManualHostOpen((o) => !o)} className="text-muted-foreground underline-offset-2 hover:underline" data-testid={`${role}-lan-coordinator-toggle`}>
                      {manualHostOpen ? "Hide coordinator address" : manualHost ? `Coordinator address: ${manualHost}` : "No internet? Enter the coordinator address"}
                    </button>
                    {manualHostOpen ? (
                      <div className="flex gap-2">
                        <Input value={manualHost} onChange={(e) => setManualHost(e.target.value)} placeholder="e.g. 192.168.0.12" className="h-10 rounded-xl bg-background" inputMode="decimal" data-testid={`${role}-lan-coordinator-input`} />
                        <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => { const v = setManualLanHost(manualHost); setManualHost(v || ""); setManualHostOpen(false); toast.success(v ? `Will look for the coordinator at ${v}` : "Coordinator address cleared"); }} data-testid={`${role}-lan-coordinator-save`}>Save</Button>
                      </div>
                    ) : null}
                    <p className="text-muted-foreground">Devices on the same Wi-Fi are found automatically; the address is only needed if discovery is blocked by the router.</p>
                  </div>
                ) : null}
              </form>
            ) : (
              <form className="space-y-4" onSubmit={handleLogin}>
                <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Business</span>
                  <span className="font-medium">{businessCode}</span>
                  {isInstalledLaunch ? (
                    <button
                      type="button"
                      className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))] hover:underline"
                      onClick={() => navigate("/", { replace: true })}
                      title="Choose a different dashboard or business"
                      data-testid={`${role}-login-switch-dashboard`}
                    >
                      Switch dashboard
                    </button>
                  ) : (
                    <button type="button" className="text-xs font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline" onClick={() => { setStage("code"); setLegacy(false); setPin(""); setLegacyPassword(""); setUsers([]); setSelectedUserId(""); }} data-testid={`${role}-login-change-business`}>Change</button>
                  )}
                </div>
                {!legacy ? (
                  <>
                    <div className="space-y-2">
                      <Label>Who's signing in?</Label>
                      <div className="max-h-56 overflow-y-auto rounded-2xl border border-border bg-background p-1" data-testid={`${role}-user-list`}>
                        {users.map((u) => {
                          const tone = ROLE_BADGE_TONE[u.role] || "bg-secondary text-muted-foreground border-border";
                          const isSel = selectedUserId === u.id;
                          return (
                            <button
                              type="button"
                              key={u.id}
                              onClick={() => setSelectedUserId(u.id)}
                              className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors ${isSel ? "bg-[hsl(var(--accent))]/15 ring-1 ring-[hsl(var(--accent))]" : "hover:bg-secondary/60"}`}
                              data-testid={`${role}-user-option-${u.id}`}
                            >
                              <span className="truncate text-sm font-medium">{u.full_name || u.first_name}</span>
                              <Badge className={`rounded-full text-[10px] capitalize ${tone}`}>{u.role}</Badge>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`${role}-pin-input`}>PIN</Label>
                      <Input
                        id={`${role}-pin-input`}
                        type="password"
                        inputMode="text"
                        maxLength={72}
                        className="h-11 rounded-xl bg-background tracking-widest"
                        value={pin}
                        onChange={(event) => setPin(event.target.value)}
                        placeholder="••••"
                        autoComplete="current-password"
                        data-testid={`${role}-pin-input`}
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor={`${role}-password-input`}>Business password</Label>
                    <Input
                      id={`${role}-password-input`}
                      type="password"
                      className="h-11 rounded-xl bg-background"
                      value={legacyPassword}
                      onChange={(event) => setLegacyPassword(event.target.value)}
                      placeholder="Enter password"
                      autoComplete="current-password"
                      data-testid={`${role}-password-input`}
                    />
                  </div>
                )}
                {/* "Remember on this device" toggle removed — sessions
                    are now strictly tab-scoped per user (sessionStorage
                    only) to guarantee complete isolation between roles
                    on a shared device. Closing the tab signs the user
                    out. Refresh keeps them signed in. */}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid={`${role}-login-submit-button`}
                >
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
                {users.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => { setLegacy((v) => !v); setPin(""); setLegacyPassword(""); }}
                    className="block w-full text-center text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    data-testid={`${role}-login-toggle-legacy`}
                  >
                    {legacy ? "Sign in as a user instead" : "Use the business password instead"}
                  </button>
                ) : null}
                {/* Session 30.35 — the "Need a different dashboard on this
                    device?" role chips were removed at the owner's request;
                    the Switch dashboard flow already covers this. */}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
